import {runInAction} from "mobx"
import {log} from "@cling/lib.shared.logging"
import {ui_state} from "../state/index"
import {report_error, report_info} from "@cling/lib.shared.debug"
import type {
    MessageFromServiceWorker,
    MessageToServiceWorker,
} from "@cling/lib.web.service_worker/message_types"
import {error_to_string, is_never_compile_time_check} from "@cling/lib.shared.utils"
import {as_BlobUID} from "@cling/lib.shared.model"

// Note: We redeclare the interface `ServiceWorkerContainer` here,
//       because we want to redefine the `postMessage` method to
//       only allow a `MessageToServiceWorker` as first parameter.
interface ServiceWorkerContainer extends EventTarget {
    readonly ready: Promise<ServiceWorkerRegistration>
    controller: {
        readonly scriptURL: string
        postMessage(message: MessageToServiceWorker, transfer?: Array<Transferable>): void
    }
    addEventListener(
        type: "message",
        listener: (event: {data: MessageFromServiceWorker}) => void,
    ): void
    addEventListener(type: "controllerchange", listener: () => void): void
}

export async function init() {
    const swc = navigator.serviceWorker as any as ServiceWorkerContainer
    if (!swc) {
        log.info("navigator.serviceWorker not defined -- running without ServiceWorker")
        return
    }
    let swr: ServiceWorkerRegistration
    try {
        swr = await cling.swr_promise
    } catch (error) {
        log.info("Failed to register ServiceWorker -- running without ServiceWorker", error)
        return
    }
    const check_for_new_version = async () => {
        try {
            await swr.update()
        } catch (error) {
            const error_str = error_to_string(error)
            if (
                error_str.includes(
                    "InvalidStateError: Failed to update a ServiceWorker for scope ('https://cling.com/') with script ('Unknown'): The object is in an invalid state",
                ) ||
                error_str.includes(
                    "Failed to update the ServiceWorker: InvalidStateError: An attempt was made to use an object that is not, or is no longer, usable",
                )
            ) {
                report_info("Failed to update the ServiceWorker", error)
            } else {
                report_error("Failed to update the ServiceWorker", error)
            }
        }
        // Schedule next update check.
        setTimeout(check_for_new_version, 17 * 60 * 1000)
    }
    // Schedule first update check.
    setTimeout(check_for_new_version, 17 * 60 * 1000)
    const not_controlled_by_service_worker = () => !swc.controller
    const controlled_by_wrong_service_worker = () =>
        swc.controller && !swc.controller.scriptURL.endsWith("service_worker.js")
    const validate_active_service_worker = () => {
        if (controlled_by_wrong_service_worker()) {
            report_error(
                `Controlled by wrong ServiceWorker: ${swc.controller.scriptURL} - trying to unregister it and reload`,
            )
            swr.unregister()
                .then(() => reload("Controlled by wrong service worker"))
                .catch((error) => report_error("Failed to unregister wrong service worker", error))
        }
    }
    validate_active_service_worker()
    let not_controlled_by_service_worker_ = not_controlled_by_service_worker()
    let controlled_by_wrong_service_worker_ = controlled_by_wrong_service_worker()

    if (controlled_by_wrong_service_worker_) {
        swc.controller!.postMessage({type: "new_dataflow"})
    }
    swc.addEventListener("controllerchange", () => {
        validate_active_service_worker()
        if (not_controlled_by_service_worker_) {
            // If the current browser tab was not controlled by a ServiceWorker before,
            // we ignore "controllerchange" event.
        } else if (controlled_by_wrong_service_worker_) {
            // If the current browser tab was controlled by a wrong ServiceWorker before,
            // we ignore "controllerchange" event.
        } else {
            log.info("Controlled by new ServiceWorker -- showing new version available hint ...")
            runInAction(() => (ui_state.show_new_version_available = true))
        }
        not_controlled_by_service_worker_ = not_controlled_by_service_worker()
        controlled_by_wrong_service_worker_ = controlled_by_wrong_service_worker()
    })
    swc.addEventListener("message", async ({data}) => {
        try {
            const msg = data as MessageFromServiceWorker
            if (msg.type === "log") {
                const {severity, context, message, extra} = msg
                log[severity](`[${context}] ${message}`, extra)
            } else if (msg.type === "refresh_image") {
                const {blob_uid, url} = msg
                ui_state.inc_image_cache_thrash_index(as_BlobUID(blob_uid))
                // This is needed for PhotoSwipe.
                const q = new URL(url).searchParams.get("q")!
                for (const elm of document.querySelectorAll(`[src*="${q}"]`)) {
                    const img = elm as HTMLImageElement
                    img.src = `${url}&_thrash=${Date.now()}`
                }
            } else {
                is_never_compile_time_check(msg)
            }
        } catch (error) {
            report_error("Failed to handle message from ServiceWorker", error)
        }
    })
}
